import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

/**
 * Camada de dados das Reservas de ambiente (Sprint 3, migration 0025).
 *
 * Regra de conflito no banco: uma restrição EXCLUDE impede duas reservas
 * `confirmada` sobrepostas no mesmo ambiente (código 23P01). A função
 * `verificar_conflitos_ambiente` alimenta o aviso ao vivo do formulário. A
 * prioridade de evento público vem do fato de que um evento público com
 * ambiente cria uma reserva vinculada (`evento_id`), que trava concorrentes.
 */
export type Reserva = Database["public"]["Tables"]["reservas"]["Row"];

export interface ReservaComRelacoes extends Reserva {
  ambiente_nome: string | null;
  /** Nome do ministério principal (primeiro da lista) — mantido para telas antigas. */
  ministerio_nome: string | null;
  /** Todos os ministérios vinculados (F2-32, revisão 20/07/2026). */
  ministerios: { id: string; nome: string }[];
}

export interface DadosReserva {
  ambienteId: string;
  titulo: string;
  inicio: string; // ISO (timestamptz)
  fim: string; // ISO (timestamptz)
  /**
   * F2-32: um ou mais ministérios responsáveis pela reserva (atividade
   * conjunta). O primeiro vira o `ministerio_id` principal da linha.
   */
  ministerioIds?: string[];
  observacoes?: string | null;
  solicitadaPorPessoaId?: string | null;
}

export interface ConflitoAmbiente {
  reservaId: string;
  titulo: string;
  inicio: string;
  fim: string;
  eventoId: string | null;
  ehPublico: boolean;
}

function traduzErroReserva(error: { code?: string } | null): Error {
  if (error?.code === "23P01") {
    return new Error("Conflito de horário: este ambiente já está reservado nesse período.");
  }
  if (error?.code === "23514") {
    return new Error("O horário de término deve ser depois do início.");
  }
  return new Error("Não foi possível salvar a reserva.");
}

export const reservasService = {
  /**
   * Todas as reservas (pendente/confirmada/cancelada), com nome do ambiente e
   * do ministério resolvidos. BUG-05: antes filtrava .neq("status","cancelada")
   * e a reserva rejeitada/cancelada simplesmente sumia da tela, sem histórico
   * nem motivo — agora a tela (GerenciarReservas) é quem decide como agrupar
   * ativas x histórico, mas o dado cancelado continua disponível.
   */
  async listar(): Promise<ReservaComRelacoes[]> {
    const [res, ministeriosRes] = await Promise.all([
      supabase.from("reservas").select("*, ambientes(nome), ministerios!reservas_ministerio_id_fkey(nome)").order("inicio", {
        ascending: true,
      }),
      // F2-32: todos os ministérios vinculados (não só o principal).
      supabase.from("reservas_ministerios").select("reserva_id, ministerios(id, nome)"),
    ]);
    if (res.error) throw new Error("Não foi possível carregar as reservas.");

    const typed = (res.data ?? []) as unknown as Array<
      Reserva & { ambientes: { nome: string } | null; ministerios: { nome: string } | null }
    >;
    const ministeriosPorReserva = new Map<string, { id: string; nome: string }[]>();
    if (!ministeriosRes.error) {
      for (const linha of (ministeriosRes.data ?? []) as unknown as Array<{
        reserva_id: string;
        ministerios: { id: string; nome: string } | null;
      }>) {
        if (!linha.ministerios) continue;
        const lista = ministeriosPorReserva.get(linha.reserva_id) ?? [];
        lista.push(linha.ministerios);
        ministeriosPorReserva.set(linha.reserva_id, lista);
      }
    }
    return typed.map((r) => ({
      ...r,
      ambiente_nome: r.ambientes?.nome ?? null,
      ministerio_nome: r.ministerios?.nome ?? null,
      ministerios: ministeriosPorReserva.get(r.id) ?? [],
    }));
  },

  /**
   * F2-32: grava a lista completa de ministérios vinculados à reserva em
   * `reservas_ministerios` (delete-then-insert, mesmo padrão de categorias
   * de livro / ministérios de evento).
   */
  async sincronizarMinisterios(reservaId: string, ministerioIds: string[] | undefined): Promise<void> {
    // F5-14: sincronização INCREMENTAL (só remove os retirados, só insere os
    // novos) — editar uma reserva sem mexer nos ministérios não toca em
    // reservas_ministerios.
    const desejados = new Set(ministerioIds ?? []);
    const { data: atuais, error: erroLer } = await supabase
      .from("reservas_ministerios")
      .select("ministerio_id")
      .eq("reserva_id", reservaId);
    if (erroLer) throw new Error("Não foi possível salvar os ministérios da reserva.");
    const existentes = new Set((atuais ?? []).map((r) => r.ministerio_id as string));
    const remover = [...existentes].filter((id) => !desejados.has(id));
    const adicionar = [...desejados].filter((id) => !existentes.has(id));
    if (remover.length) {
      const { error } = await supabase
        .from("reservas_ministerios")
        .delete()
        .eq("reserva_id", reservaId)
        .in("ministerio_id", remover);
      if (error) throw new Error("Não foi possível salvar os ministérios da reserva.");
    }
    if (adicionar.length) {
      const { error } = await supabase
        .from("reservas_ministerios")
        .insert(adicionar.map((ministerioId) => ({ reserva_id: reservaId, ministerio_id: ministerioId })));
      if (error) throw new Error("Não foi possível salvar os ministérios da reserva.");
    }
  },

  /** Verifica conflitos de um ambiente num intervalo (aviso do formulário). */
  async verificarConflitos(
    ambienteId: string,
    inicioISO: string,
    fimISO: string,
    ignorarReservaId?: string | null,
  ): Promise<ConflitoAmbiente[]> {
    const { data, error } = await supabase.rpc("verificar_conflitos_ambiente", {
      p_ambiente_id: ambienteId,
      p_inicio: inicioISO,
      p_fim: fimISO,
      p_ignorar_reserva: ignorarReservaId ?? undefined,
    });
    if (error) throw new Error("Não foi possível verificar conflitos de horário.");

    const rows = (data ?? []) as Array<{
      reserva_id: string;
      titulo: string;
      inicio: string;
      fim: string;
      evento_id: string | null;
      eh_publico: boolean;
    }>;
    return rows.map((c) => ({
      reservaId: c.reserva_id,
      titulo: c.titulo,
      inicio: c.inicio,
      fim: c.fim,
      eventoId: c.evento_id,
      ehPublico: c.eh_publico,
    }));
  },

  /**
   * Sempre tenta inserir como 'confirmada' — quem não administra o módulo
   * Reservas/Eventos é rebaixado para 'pendente' automaticamente por um
   * trigger no banco (migration 0029), não por lógica aqui no cliente.
   * Retorna o status final para a tela decidir a mensagem certa.
   */
  async criar(dados: DadosReserva): Promise<{ status: string }> {
    const { data, error } = await supabase
      .from("reservas")
      .insert({
        ambiente_id: dados.ambienteId,
        titulo: dados.titulo,
        inicio: dados.inicio,
        fim: dados.fim,
        ministerio_id: dados.ministerioIds?.[0] ?? null,
        observacoes: dados.observacoes ?? null,
        solicitada_por_pessoa_id: dados.solicitadaPorPessoaId ?? null,
        status: "confirmada",
      })
      .select("id, status")
      .single();
    if (error || !data) throw traduzErroReserva(error);
    await this.sincronizarMinisterios(data.id, dados.ministerioIds);
    return { status: data.status };
  },

  async atualizar(id: string, dados: DadosReserva): Promise<void> {
    const { error } = await supabase
      .from("reservas")
      .update({
        ambiente_id: dados.ambienteId,
        titulo: dados.titulo,
        inicio: dados.inicio,
        fim: dados.fim,
        ministerio_id: dados.ministerioIds?.[0] ?? null,
        observacoes: dados.observacoes ?? null,
      })
      .eq("id", id);
    if (error) throw traduzErroReserva(error);
    await this.sincronizarMinisterios(id, dados.ministerioIds);
  },

  /**
   * C-03 (reteste 23-24/07/2026): cancelar agora passa pela RPC
   * cancelar_reserva, com motivo (mesmo padrão do cancelamento de Eventos —
   * antes era um UPDATE direto sem motivo e sem notificação nenhuma).
   * Libera o horário (sai do EXCLUDE, que só vale para 'confirmada').
   */
  async cancelar(id: string, motivo?: string | null): Promise<void> {
    const { error } = await supabase.rpc("cancelar_reserva", {
      p_reserva_id: id,
      p_motivo: motivo ?? undefined,
    });
    if (error) throw new Error(error.message || "Não foi possível cancelar a reserva.");
  },

  async excluir(id: string): Promise<void> {
    const { error } = await supabase.from("reservas").delete().eq("id", id);
    if (error) throw new Error("Não foi possível excluir a reserva.");
  },

  /** Aprova uma solicitação pendente — só quem administra Reservas/Eventos. */
  async aprovar(id: string): Promise<void> {
    const { error } = await supabase.rpc("aprovar_reserva", { p_reserva_id: id });
    if (error) throw new Error(error.message || "Não foi possível aprovar a reserva.");
  },

  /** Rejeita uma solicitação pendente, com motivo opcional. */
  async rejeitar(id: string, motivo?: string | null): Promise<void> {
    const { error } = await supabase.rpc("rejeitar_reserva", {
      p_reserva_id: id,
      p_motivo: motivo ?? undefined,
    });
    if (error) throw new Error(error.message || "Não foi possível recusar a reserva.");
  },
};
